import { getAppName, isDeveloper } from "../AppType";
import { UserTab } from "../components/UserHeartbeat/service";
import { unique } from "../utils";
import { text } from "../utils/i18n";
import { privateUserId } from "../utils/user";
import { BooleanOrAny } from "./BooleanOrAny";
import { CallTag } from "./CallTag";
import { Country } from "./Country";
import { LeadListMode, translateLeadListMode } from "./LeadListMode";
import Sort from "./Sort";
import { Users } from "./Users";

export enum CallConnectionState {
  Any = 'any',

  /**
   * Queries calls which are either connecting or connected with a user.
   */
  Active = 'active',

  /**
   * Queries calls which are connected with a user.
   */
  Connected = 'connected',

  /**
   * Queries calls which are disconnected.
   */
  Disconnected = 'disconnected'
}

export class CallFilters {
  readonly search?: string;
  readonly sort = new Sort({ asc: false, field: 'date' });
  /**
   * For admin only.
   */
  readonly organizationIds?: string[];
  readonly dialGroupIds?: string[];
  readonly shuffleDialGroupIds?: string[];

  readonly after?: Date;
  readonly "after.startOf"?: RelativeDateFrom;
  readonly "after.add"?: string;

  get "after.value"(): number {
    return parseFloat(this["after.add"]?.split(" ", 2)[0]);
  }

  get "after.unit"(): RelativeDateUnit {
    return this["after.add"]?.split(" ", 2)[1] as RelativeDateUnit;
  }

  readonly before?: Date;
  readonly "before.startOf"?: RelativeDateFrom;
  readonly "before.add"?: string;

  get "before.value"(): number {
    return parseFloat(this["before.add"]?.split(" ", 2)[0]);
  }

  get "before.unit"(): RelativeDateUnit {
    return this["before.add"]?.split(" ", 2)[1] as RelativeDateUnit;
  }

  readonly ids: string[] = [];
  readonly caller: string[] = [];
  readonly recipients: string[] = [];
  readonly userIds: string[] = [];
  readonly teamIds: string[] = [];

  /**
   * Whether to exclude `userIds`.
   */
  readonly excludeUserIds?: boolean;

  /**
   * Whether both ends have answered the call.
   */
  readonly isConnected?: boolean;

  /**
   * Whether the contact has answered the call. In predictive mode, the call might
   * not have been answered by a user.
   */
  readonly isContactAnswer?: boolean;
  readonly allTagIds: string[] = [];
  readonly anyTagId: string[] = [];
  readonly notAnyTagId: string[] = [];
  readonly notAllTagIds: string[] = [];
  readonly noTag?: boolean;
  readonly contactIds: string[] = [];
  readonly easyTelecomUserCallIds: string[] = [];
  readonly offset: number = 0;
  readonly count: number = 50;
  readonly isInbound?: boolean;
  readonly connectionState?: CallConnectionState = CallConnectionState.Disconnected;
  /**
   * Relevant when searching for phone number as free-text.
   */
  readonly country?: Country;
  readonly projectIds?: string[];
  readonly excludeProjectIds?: boolean;
  readonly previousCallCount?: number;
  readonly previousCallCountMin?: number;
  readonly previousCallCountMax?: number;
  readonly leadListModes: LeadListMode[] = [];
  readonly voicemail?: boolean;
  readonly connectedDurationMin?: number;
  readonly connectedDurationMax?: number;
  readonly totalDurationMin?: number;
  readonly totalDurationMax?: number;
  readonly collectionId?: string;
  readonly isLatest?: boolean;
  readonly isRecordingAvailable?: boolean;
  /**
   * @deprecated Utveckling av call_stats.
   */
  readonly optimize?: boolean;
  readonly isExternal?: boolean;
  readonly hasTranscript?: boolean;

  /**
   * UI only!
   */
  readonly userActiveNow?: BooleanOrAny;

  constructor(deriveFrom?: Partial<CallFilters>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (typeof this.previousCallCountMin === 'number' && this.previousCallCountMax < this.previousCallCountMin) {
      this.previousCallCountMax = this.previousCallCountMin;
    }
  }

  /**
   * Returns a new instance with userIds set.
   */
  withUserTabs(tabs: UserTab[], allUserIds: string[]) {
    let userIds = this.userIds;
    if (userIds?.length) {
      allUserIds = allUserIds.filter(userId => userIds.includes(userId));
    }

    if (this.userActiveNow === BooleanOrAny.True) {
      userIds = tabs.filter(tab =>
        allUserIds.includes(tab.userId) && tab.isUserActive
      ).map(tab => tab.userId);

      userIds = unique(userIds);

    } else if (this.userActiveNow === BooleanOrAny.False) {
      userIds = allUserIds.filter(userId => !tabs.find(tab => tab.userId === userId && tab.isUserActive));
    }

    return new CallFilters({
      ...this,
      userIds
    });
  }

  toQuery() {
    return {
      ...this,
      ...this.sort?.toQuery(),
      sort: undefined,
      s: this.search,
      debug: isDeveloper, // Whether to return debug information.
    };
  }
}

export type ListCallFilterDescriptionsParams = {
  /**
   * What to call the "none" user.
   */
  privateUserName?: string;

  callTags?: ById<CallTag>;
  users?: Users;
}

export function listCallFilterDescriptions(filters: Partial<CallFilters>, args: ListCallFilterDescriptionsParams): string[] {
  const descriptions: string[] = [];

  /*
  //  Relative date descriptions first to not confuse the combination of dates and tags, etc.
  const beforeRelativeDescription = translateRelativeDate(
    filters.before as RelativeDate,
    "before"
  );

  const afterRelativeDescription = translateRelativeDate(
    filters.after as RelativeDate,
    "after"
  );

  if (beforeRelativeDescription) {
    descriptions.push(beforeRelativeDescription);

  } else if (afterRelativeDescription) {
    descriptions.push(afterRelativeDescription)
  }
    */

  filters.allTagIds?.forEach(tagId => {
    const tag = args.callTags?.[tagId];

    if (tag) {
      descriptions.push(tag.name);
    }
  });

  filters.anyTagId?.forEach(tagId => {
    const tag = args.callTags?.[tagId];

    if (tag) {
      descriptions.push(tag.name);
    }
  });

  let exactCount = filters.previousCallCount;

  if (typeof filters.previousCallCountMin === 'number' &&
    filters.previousCallCountMin === filters.previousCallCountMax) {
    exactCount = filters.previousCallCountMin;
  }

  if (filters.previousCallCount === 0) {
    descriptions.push(text`1 samtalsförsök`);

  } else if (typeof filters.previousCallCount === 'number') {
    descriptions.push(text`[plural] ${filters.previousCallCount + 1} samtalsförsök`);

  } else if (typeof filters.previousCallCountMin === 'number' &&
    typeof filters.previousCallCountMax === 'number') {
    descriptions.push(text`[plural] ${(filters.previousCallCountMin + 1) + ' - ' + (filters.previousCallCountMax + 1)} samtalsförsök`);

  } else if (typeof filters.previousCallCountMin === 'number') {
    descriptions.push(text`[plural] ${(filters.previousCallCountMin + 1) + '+'} samtalsförsök`);

  } else if (typeof filters.previousCallCountMax === 'number') {
    descriptions.push(text`[plural] ${'1 - ' + (filters.previousCallCountMax + 1)} samtalsförsök`);
  }

  if (filters.isInbound === true) {
    descriptions.push(text`ingående`);

  } else if (filters.isInbound === false) {
    descriptions.push(text`utgående`);
  }

  if (filters.isConnected === true) {
    descriptions.push(text`kopplade`);

  } else if (filters.isConnected === false) {
    descriptions.push(text`ej kopplade`);
  }

  if (filters.isContactAnswer === true && !filters.isConnected) {
    descriptions.push(text`kontakt svarat`);

  } else if (filters.isContactAnswer === false) {
    descriptions.push(text`kontakt ej svar`);
  }

  if (filters.voicemail === true) {
    descriptions.push(text`röstbrevlåda`);

  } else if (filters.voicemail === false) {
    descriptions.push(text`ej röstbrevlåda`);
  }

  if (filters.isLatest === true) {
    descriptions.push(text`senaste`);

  } else if (filters.isLatest === false) {
    descriptions.push(text`ej senaste`);
  }


  if (filters.leadListModes) {
    unique(filters.leadListModes.map(mode => translateLeadListMode(mode)))
      .forEach(string => descriptions.push(string));
  }

  if (filters.userIds?.length) {
    let summary: string;

    if (filters.userIds.length > 3) {
      summary = text`${filters.userIds} användare`;

    } else {
      summary = filters.userIds.map(userId => {
        if (userId === privateUserId) {
          return args.privateUserName ?? getAppName();
        }

        return args.users?.getPreferredName(userId);
      }).filter(name => name).join(', ');
    }

    if (filters.excludeUserIds && summary) {
      descriptions.push(text`ej ${summary}`);

    } else {
      descriptions.push(summary);
    }

  } else if (filters.userActiveNow === BooleanOrAny.True) {
    descriptions.push(text`vid datorn`);

  } else if (filters.userActiveNow === BooleanOrAny.False) {
    descriptions.push(text`inte vid datorn`);
  }

  const minDuration = Math.min(filters?.totalDurationMin ?? Number.MAX_VALUE, filters?.connectedDurationMin ?? Number.MAX_VALUE);
  const maxDuration = Math.min(filters?.totalDurationMax ?? Number.MAX_VALUE, filters?.connectedDurationMax ?? Number.MAX_VALUE);
  if (maxDuration && maxDuration !== Number.MAX_VALUE) {
    descriptions.push(`${Math.round(minDuration / 1000) ?? 0}s - ${Math.round(maxDuration / 1000)}s`);

  } else if (minDuration && minDuration !== Number.MAX_VALUE) {
    descriptions.push(text`Minst ${Math.round(minDuration / 1000) + 's'}`);
  }

  if (filters.isRecordingAvailable) {
    descriptions.push(text`har inspelning`);

  } else if (filters.isRecordingAvailable === false) {
    descriptions.push(text`saknar inspelning`);
  }


  if (filters.hasTranscript === true) {
    descriptions.push(text`Har transkript`);

  } else if (filters.hasTranscript === false) {
    descriptions.push(text`Saknar transkript`);
  }


  if (filters.isExternal) {
    descriptions.push(text`externt`);

  } else if (filters.isExternal === false) {
    descriptions.push(text`ej externt`);
  }

  if (isDeveloper && filters.optimize === true) {
    descriptions.push(text`Optimera`);

  } else if (isDeveloper && filters.optimize === false) {
    descriptions.push(text`Optimera ej`);
  }

  return descriptions;
}

/**
 * @returns Whether any non-default filter is present, i.e. if the user has entered a filter. 
 */
export function isCallFiltersEmpty(filters: Partial<CallFilters> | null | undefined) {
  if (!filters) {
    return null;  //  Must return null in VariableList.
  }

  return !(
    filters.search || filters.allTagIds?.length || filters.anyTagId?.length ||
    filters.userIds?.length || filters.teamIds?.length || filters.dialGroupIds?.length ||
    filters.after || filters.before || filters.caller?.length || filters.recipients?.length ||
    typeof filters.isLatest === 'boolean' || typeof filters.isRecordingAvailable === 'boolean' ||
    typeof filters.hasTranscript === 'boolean'
  );
}